import React, { useState } from "react";
import Menu from "@mui/material/Menu";
import Tooltip from "@mui/material/Tooltip";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ShareIcon from "@mui/icons-material/Share";
import { colors } from "../../../utils";
import { ChatBadge, HEADER_ICON_SIZE, IconWrapper } from "./styles";
import { useDockUnread } from "../../ChatDock/dockStore";
import HomeNavEntry from "./HomeNavEntry";
import CaptureNavEntry from "./CaptureNavEntry";
import ChatNavEntry from "./ChatNavEntry";
import ExploreNavEntry from "./ExploreNavEntry";
import { NavEntryMenuItem } from "./NavEntryMenuItem";

/**
 * The folded header rows (Home -> Discover -> Capture -> Messages -> Share
 * Gallery), rendered from ONE place so the ⋮ menu below and the avatar
 * ("Account menu") dropdown can never drift apart.
 *
 * T22.615 (Gordon, 2026-10-21, phone video VID-20261021-0917): below the
 * tabletP cut the header showed TWO menus — this ⋮ (Gallery/Discover/Capture/
 * Messages) and the avatar menu (Gallery/Themes/Media Library/…) — so Gallery
 * was listed twice. GalleryHeader now renders these rows INSIDE the avatar
 * dropdown (ProfileMenu `foldedNav`) with includeHome={false}, because the
 * avatar menu already carries its own Gallery row, and no longer mounts the
 * separate ⋮ trigger. See GalleryHeader.oneMenu.t22615.test.jsx.
 */
export const HeaderFoldRows = ({
  closeMenu,
  onShareGallery,
  showShareGallery,
  includeHome = true,
}) => (
  <>
    {/* No "Give Feedback" row: the Feedback entry was removed from the
        header on BOTH surfaces (Gordon, 2026-08-01). Settings > FAQ >
        "Feedback form" opens the same SocialFoxFeedbackForm. */}
    {/* QA-149 (Kelly, 2026-08-19): row order mirrors the desktop cluster
        in GalleryHeader.jsx — Home -> Discover -> Capture -> Messages —
        so the fold can never silently drift from the bar (same invariant
        this file's own header comment already states). */}
    {includeHome ? (
      <HomeNavEntry variant="menu" closeMenu={closeMenu} />
    ) : null}
    <ExploreNavEntry variant="menu" closeMenu={closeMenu} />
    <CaptureNavEntry variant="menu" closeMenu={closeMenu} />
    <ChatNavEntry variant="menu" closeMenu={closeMenu} />
    {/* Following (Follower Feed) row — REMOVED (Gordon, 2026-08-06), see
        GalleryHeader.jsx for the matching desktop-cluster removal and
        GalleryHeader.noFollowingEntry.test.jsx for the regression guard. */}
    {/* bug #947: "Share Gallery" moved here from the Profile card,
        mirroring the bar's Share-Gallery-then-Invite order and this
        file's own stated invariant ("cannot be present in the bar and
        missing from the fold"). */}
    {showShareGallery ? (
      <NavEntryMenuItem
        label="Share Gallery"
        onActivate={onShareGallery}
        closeMenu={closeMenu}
        icon={
          <ShareIcon
            aria-hidden="true"
            style={{ color: colors.PrimaryPurple, fontSize: 22 }}
          />
        }
      />
    ) : null}
  </>
);

/**
 * The phone-cut overflow ("⋮") menu for GalleryHeader.
 *
 * WHY: measured on the deployed staging bundle at 390px — the header's right
 * cluster alone needs 444px, so the bar rendered 602px wide against a 376px
 * client width (226px of page overflow on /settings, /analytics, /referrals)
 * and, on routes whose shell clips instead of scrolling (/capture,
 * /notifications), pushed the avatar off-screen at x=502 where nothing could
 * reach it. The responsive system's phone row calls for a slim
 * "logo + bell + avatar" bar with the secondary actions collapsed into one ⋮
 * menu; this is that menu.
 *
 * Each remaining entry renders here through its OWN component in
 * `variant="menu"` form, so the feature flag, the label and the click action
 * are read from exactly one place per entry — an entry cannot be present in the
 * bar and missing from the fold, or fire a different action in the two.
 *
 * T22.615: GalleryHeader no longer mounts this ⋮ — its rows now live in the
 * avatar dropdown (see HeaderFoldRows above). Kept for the fold-row contract
 * tests that drive the rows through it.
 *
 * Rendered only below the tabletP cut (<=899px — see OverflowOnly in
 * styles.js; widened from the phone-only cut by gallery-header-768-tablet-
 * overflow), so tablet-landscape/desktop keep today's geometry untouched.
 */
const HeaderOverflowMenu = ({ onShareGallery, showShareGallery }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const close = () => setAnchorEl(null);
  // fix-1003-vy-0914-nc-all-messages-unread-badge-invisible-at-390: ChatNavEntry's
  // own ChatBadge only exists on the desktop "icon" variant, whose entire
  // SecondaryActions wrapper is `display:none` below the phone cut (see the
  // comment on that wrapper in GalleryHeader.jsx) -- so at <=390px the unread
  // badge was never just hidden, its host node didn't exist in the layout at
  // all, and this trigger (the only chat-related element still on-screen) gave
  // no visual cue that Messages had unread until the menu was opened. Mirror
  // the same ChatBadge here so the fold has an equivalent indicator.
  const chatUnread = useDockUnread();

  return (
    <>
      <Tooltip title="More">
        <IconWrapper
          className="header-overflow-button"
          role="button"
          tabIndex={0}
          aria-label={
            chatUnread ? `More actions, ${chatUnread} unread messages` : "More actions"
          }
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setAnchorEl(e.currentTarget);
            }
          }}
          style={{ position: "relative", cursor: "pointer" }}
        >
          <MoreVertIcon
            aria-hidden="true"
            style={{ color: colors.PrimaryPurple, fontSize: HEADER_ICON_SIZE }}
          />
          {chatUnread ? (
            <ChatBadge aria-hidden="true">{chatUnread}</ChatBadge>
          ) : null}
        </IconWrapper>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ "aria-label": "More actions" }}
        slotProps={{ paper: { sx: { minWidth: 200, borderRadius: "8px" } } }}
      >
        <HeaderFoldRows
          closeMenu={close}
          onShareGallery={onShareGallery}
          showShareGallery={showShareGallery}
        />
      </Menu>
    </>
  );
};

export default HeaderOverflowMenu;
